import React from 'react';
import {
  BodyText,
  BoldBodyText,
  Button,
  FlexRow,
  FormCell,
  Grid,
  Header3Text,
  NavigationItem,
  Notification,
  RotatingIcon,
} from 'ui-toolkit';
import { IFetchStatus } from 'types/rest/IFetchStatus';
import { ITitleSearchRequestData } from '../../../types/order/title/ITitleSearchRequest';
import { activeStatuses, eTitleSearchStatus } from '../../../types/order/title/eTitleSearchStatus';
import { IRedVisionSearchData } from '../../../types/order/title/IRedVisionSearch';
import { Separator } from './Separator';
import { IconType } from 'ui-toolkit';
import { useSelectCurrentOrder } from 'selectors/order/selectCurrentOrder';
import { DateFormat, getDateString } from '../../../ui-toolkit/utilities/getDateString';

export interface ITitleSearchComponentProps {
  titleSearchRequests: ITitleSearchRequestData[];
  titleSearchRequestFetchStatus: IFetchStatus;
  orderId: string;
  renderTitleIssuedAgentButton: boolean;
  redVisionSearch?: IRedVisionSearchData;
  onCreateNewTitleSearch: () => void;
  onCancelTitleSearch: (id: string) => void;
  onCancelFetchStatus: IFetchStatus;
  onCreateFetchStatus: IFetchStatus;
  onCreateError: string | null;
  onCancelError: string | null;
}
export const TitleSearchComponent: React.FC<ITitleSearchComponentProps> = ({
  titleSearchRequests,
  titleSearchRequestFetchStatus,
  onCreateNewTitleSearch,
  onCancelTitleSearch,
  orderId,
  renderTitleIssuedAgentButton,
  redVisionSearch,
  onCancelFetchStatus,
  onCreateFetchStatus,
  onCreateError,
  onCancelError,
}) => {
  const noActiveTitleRequests =
    titleSearchRequests.slice().filter((search) => activeStatuses.includes(search.status)).length === 0;
  const sortedTitleSearch = titleSearchRequests.slice().sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1));
  const initialTitleSearch = sortedTitleSearch
    .filter((search) => [eTitleSearchStatus.accepted, eTitleSearchStatus.sent].includes(search.status))
    .pop();
  const showInitialTitle = (search: ITitleSearchRequestData): boolean =>
    initialTitleSearch ? search.titleSearchId === initialTitleSearch.titleSearchId : true;
  const lastTitleSearch = sortedTitleSearch[0];
  const lastCompletedSearch =
    lastTitleSearch && lastTitleSearch.status === eTitleSearchStatus.accepted ? lastTitleSearch : undefined;

  const { data } = useSelectCurrentOrder();
  const isNYOrder = data.property.state === 'NY';
  return (
    <Grid>
      {titleSearchRequestFetchStatus.fetching || onCancelFetchStatus.fetching || onCreateFetchStatus.fetching ? (
        <RotatingIcon type={IconType.Loading} />
      ) : null}
      {onCancelError || onCreateError ? <Notification type={'error'} message={'Error title search'} /> : null}
      {onCreateFetchStatus.fetched && !onCreateError && (
        <Notification type={'success'} message={'Title search request sent to vendor'} />
      )}
      {onCancelFetchStatus.fetched && !onCancelError && (
        <Notification type={'success'} message={'Title search request canceled'} />
      )}
      {lastCompletedSearch && (
        <>
          <Header3Text>Status</Header3Text>
          <AcceptedStatus
            isInitialSearch={showInitialTitle(lastCompletedSearch)}
            updatedAt={lastCompletedSearch.updatedAt}
          />
        </>
      )}
      {titleSearchRequestFetchStatus.fetched && noActiveTitleRequests && (
        <CreateNewOrder
          onCreateNewTitleSearch={onCreateNewTitleSearch}
          createUpdate={!!initialTitleSearch}
          orderId={orderId}
          redVisionSearch={redVisionSearch}
          isNYorder={isNYOrder}
        />
      )}
      {sortedTitleSearch.length === 0 ? null : (
        <>
          {lastCompletedSearch ? null : <Header3Text>Status</Header3Text>}
          {sortedTitleSearch.map((search, index) => (
            <React.Fragment key={search.titleSearchId}>
              {search.status === eTitleSearchStatus.new ? (
                <OrderedTitleSearch
                  key={search.titleSearchId}
                  titleSearchRequest={search}
                  isInitialSearch={showInitialTitle(search)}
                  onCancelSearch={onCancelTitleSearch}
                  renderTitleIssuedAgentText={renderTitleIssuedAgentButton}
                />
              ) : search.status === eTitleSearchStatus.inProgress ? (
                <TitleSearchInProgress
                  key={search.titleSearchId}
                  titleSearchRequest={search}
                  isInitialSearch={showInitialTitle(search)}
                  onCancelSearch={onCancelTitleSearch}
                />
              ) : search.status === eTitleSearchStatus.cancelled ? (
                <CancelledSearches
                  key={search.titleSearchId}
                  titleSearchRequest={search}
                  isInitialSearch={showInitialTitle(search)}
                />
              ) : search.status === eTitleSearchStatus.sent ? (
                <SentStatus
                  titleSearch={search}
                  onCancelSearch={onCancelTitleSearch}
                  isInitialSearch={showInitialTitle(search)}
                />
              ) : search.status === eTitleSearchStatus.accepted ? (
                <>
                  {lastCompletedSearch === search ? null : (
                    <CompletedSearches
                      key={search.titleSearchId}
                      hideOrderSection={index !== 0}
                      titleSearchRequest={search}
                      isInitialSearch={showInitialTitle(search)}
                      onCreateNewTitleSearch={onCreateNewTitleSearch}
                      orderId={orderId}
                      redVisionSearch={redVisionSearch}
                      isNYOrder={isNYOrder}
                    />
                  )}
                </>
              ) : null}
              {index === sortedTitleSearch.length ? null : <Separator />}
            </React.Fragment>
          ))}
        </>
      )}
    </Grid>
  );
};

const getTitleSearchRequestHintText = (titleSearchRequest: ITitleSearchRequestData): string => {
  return `By: ${
    titleSearchRequest.orderedByFullName ? titleSearchRequest.orderedByFullName : 'Spruce'
  } on ${getDateString(DateFormat.hugeDateTime, titleSearchRequest.createdAt, true)}`;
};

const CreateNewOrder: React.FC<{
  onCreateNewTitleSearch: () => void;
  createUpdate: boolean;
  orderId: string;
  redVisionSearch?: IRedVisionSearchData;
  isNYorder: boolean;
}> = ({ onCreateNewTitleSearch, createUpdate, orderId, redVisionSearch, isNYorder }) => (
  <Grid>
    <FlexRow>
      {' '}
      <Button intent={'primary'} disabled={isNYorder} onClick={() => onCreateNewTitleSearch()}>
        {createUpdate ? 'Order update title Search Via KSS' : 'Order Via KSS'}
      </Button>
    </FlexRow>
    {redVisionSearch ? (
      <FormCell
        hintText={`Ordered on: ${getDateString(
          DateFormat.hugeDateTime,
          redVisionSearch.createdAt,
          true
        )} \n Priority: ${redVisionSearch.priority} ${
          redVisionSearch.instructions ? `\n Instructions: ${redVisionSearch.instructions}` : ''
        }`}
      >
        <Header3Text>Ordered Via RedVision</Header3Text>
      </FormCell>
    ) : (
      <BodyText>
        If NY, new title order requests will need to be emailed to{' '}
        <NavigationItem
          isInline
          href={`mailto:order@datatracetitle.com`}
          isActive={true}
          text={'order@datatracetitle.com'}
        />{' '}
        and updates will need to go to{' '}
        <NavigationItem
          isInline
          href={`mailto:update@datatracetitle.com`}
          isActive={true}
          text={'update@datatracetitle.com'}
        />
      </BodyText>
    )}
  </Grid>
);

const OrderedTitleSearch: React.FC<{
  titleSearchRequest: ITitleSearchRequestData;
  isInitialSearch: boolean;
  onCancelSearch: (id: string) => void;
  renderTitleIssuedAgentText: boolean;
}> = ({ titleSearchRequest, isInitialSearch, onCancelSearch, renderTitleIssuedAgentText }) => (
  <Grid>
    {renderTitleIssuedAgentText ? (
      <BodyText>In exception cases, with prior approval, search may be ordered via KSS</BodyText>
    ) : null}
    <FormCell wrapInput={false} hintText={getTitleSearchRequestHintText(titleSearchRequest)}>
      <BoldBodyText>{isInitialSearch ? 'Ordered via KSS' : 'Updated Search Ordered via KSS'}</BoldBodyText>
    </FormCell>
    <FlexRow>
      <Button intent={'secondary'} onClick={() => onCancelSearch(titleSearchRequest.titleSearchId)}>
        Cancel
      </Button>
    </FlexRow>
  </Grid>
);

const TitleSearchInProgress: React.FC<{
  titleSearchRequest: ITitleSearchRequestData;
  isInitialSearch: boolean;
  onCancelSearch: (id: string) => void;
}> = ({ titleSearchRequest, isInitialSearch, onCancelSearch }) => (
  <Grid>
    <FormCell
      wrapInput={false}
      hintText={`Last updated on ${getDateString(DateFormat.hugeDateTime, titleSearchRequest.updatedAt, true)}`}
    >
      <BoldBodyText>In Progress By KSS</BoldBodyText>
    </FormCell>
    <FlexRow>
      <Button intent={'primary'} onClick={() => onCancelSearch(titleSearchRequest.titleSearchId)}>
        Cancel
      </Button>
    </FlexRow>
    <Separator />
    <FormCell wrapInput={false} hintText={getTitleSearchRequestHintText(titleSearchRequest)}>
      <BoldBodyText>{isInitialSearch ? 'Ordered via KSS' : 'Updated Search Ordered via KSS'}</BoldBodyText>
    </FormCell>
  </Grid>
);

const CancelledSearches: React.FC<{
  titleSearchRequest: ITitleSearchRequestData;
  isInitialSearch: boolean;
}> = ({ titleSearchRequest, isInitialSearch }) => (
  <Grid>
    <FormCell
      wrapInput={false}
      hintText={`on ${getDateString(DateFormat.hugeDateTime, titleSearchRequest.updatedAt, true)}`}
    >
      <BoldBodyText>Search by KSS Cancelled</BoldBodyText>
    </FormCell>
    <Separator />
    <FormCell wrapInput={false} hintText={getTitleSearchRequestHintText(titleSearchRequest)}>
      <BoldBodyText>{isInitialSearch ? 'Ordered via KSS' : 'Updated Search Ordered via KSS'}</BoldBodyText>
    </FormCell>
  </Grid>
);

const CompletedSearches: React.FC<{
  titleSearchRequest: ITitleSearchRequestData;
  isInitialSearch: boolean;
  onCreateNewTitleSearch: () => void;
  hideOrderSection: boolean;
  orderId: string;
  redVisionSearch?: IRedVisionSearchData;
  isNYOrder: boolean;
}> = ({
  titleSearchRequest,
  isInitialSearch,
  onCreateNewTitleSearch,
  hideOrderSection,
  orderId,
  redVisionSearch,
  isNYOrder,
}) => (
  <Grid>
    <AcceptedStatus isInitialSearch={isInitialSearch} updatedAt={titleSearchRequest.updatedAt} />
    {hideOrderSection ? null : (
      <CreateNewOrder
        onCreateNewTitleSearch={onCreateNewTitleSearch}
        createUpdate={true}
        orderId={orderId}
        redVisionSearch={redVisionSearch}
        isNYorder={isNYOrder}
      />
    )}
    <Separator />
    <FormCell wrapInput={false} hintText={getTitleSearchRequestHintText(titleSearchRequest)}>
      <BoldBodyText>{isInitialSearch ? 'Ordered via KSS' : 'Updated Search Ordered via KSS'}</BoldBodyText>
    </FormCell>
  </Grid>
);

const AcceptedStatus: React.FC<{ isInitialSearch: boolean; updatedAt?: string | null }> = ({
  isInitialSearch,
  updatedAt,
}) => (
  <FormCell wrapInput={false} hintText={`Sent on ${getDateString(DateFormat.hugeDateTime, updatedAt, true)}`}>
    <BoldBodyText>{isInitialSearch ? 'Initial' : 'Updated'} Search Accepted by KSS</BoldBodyText>
  </FormCell>
);

const SentStatus: React.FC<{
  isInitialSearch: boolean;
  titleSearch: ITitleSearchRequestData;
  onCancelSearch: (id: string) => void;
}> = ({ isInitialSearch, titleSearch, onCancelSearch }) => (
  <>
    <FormCell
      wrapInput={false}
      hintText={`Sent on ${getDateString(DateFormat.hugeDateTime, titleSearch.updatedAt, true)}`}
    >
      <BoldBodyText>{isInitialSearch ? 'Initial' : 'Updated'} Search Sent to Spruce by KSS</BoldBodyText>
    </FormCell>
    <FlexRow>
      <Button intent={'secondary'} onClick={() => onCancelSearch(titleSearch.titleSearchId)}>
        Cancel
      </Button>
    </FlexRow>
    <Separator />
    <FormCell wrapInput={false} hintText={getTitleSearchRequestHintText(titleSearch)}>
      <BoldBodyText>{isInitialSearch ? 'Ordered via KSS' : 'Updated Search Ordered via KSS'}</BoldBodyText>
    </FormCell>
  </>
);
